import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment-timezone';
import CommonHeader from '../../../util_components/common_header';
import CustomSingleSelect from '../../../util_components/custom_single_select';
import CustomDatePicker from '../../../util_components/custom_date_picker';
import Paginator from '../../../util_components/paginator';

import { get_date_filter_data } from '../../../../util_functions';
import { day_format_year, time_format, date_drop_for_report } from '../../../../constants';
import { get_api } from '../../../../redux/api_funcs';
import { get_api_url } from '../../../../utils/urls';
import EventsModal from './events_modal';
import { parse_filters, build_search, offset_for_page, PAGE_SIZE } from './filters';
import {
  step_label,
  category_label,
  origins_label,
  fmt_amount,
  reason_label,
  student_label,
  is_actionable
} from './helpers';

import 'react-datepicker/dist/react-datepicker.css';

class PaymentFailures extends Component {
  constructor(props) {
    super(props);
    // The URL is the source of truth on entry, so a shared link lands on the same filters.
    this.state = {
      loading: true,
      rows: [],
      total: 0,
      open_group: null,
      ...parse_filters(props.location.search, moment().format('YYYY-MM-DD'))
    };
  }

  componentDidMount() {
    this.load_data();
  }

  // Mirrors filters into the URL; replace, not push, so paging doesn't fill the back button.
  sync_url = () => {
    const { date_filter_type, start_date, end_date, page } = this.state;
    const search = build_search({ date_filter_type, start_date, end_date, page });
    if (search !== this.props.location.search) {
      this.props.history.replace({ pathname: this.props.location.pathname, search });
    }
  };

  load_data = (page = this.state.page) =>
    this.setState({ loading: true, page }, () => {
      this.sync_url();
      const { date_filter_type, start_date, end_date } = this.state;

      const dates = get_date_filter_data(date_filter_type, start_date, end_date);
      const qp = new URLSearchParams();
      // The endpoint takes plain dates and covers the whole end day itself.
      qp.set('start_date', String(dates.final_start_date).slice(0, 10));
      qp.set('end_date', String(dates.final_end_date).slice(0, 10));
      qp.set('limit', String(PAGE_SIZE));
      qp.set('offset', String(offset_for_page(page)));

      const url = get_api_url(`/v2/cms_user/payment_failures?${qp.toString()}`, false, true);

      get_api(url, true)
        .then((res) => {
          const data = (res && res.data && res.data.data) || {};
          this.setState({ loading: false, rows: data.rows || [], total: data.total || 0 });
        })
        .catch(() => this.setState({ loading: false, rows: [], total: 0 }));
    });

  change_handler = (e) =>
    this.setState({ [e.target.name]: e.target.value, page: 1 }, () => this.load_data(1));

  handle_date_change = (key, value) =>
    this.setState({ [key]: moment(value).format('YYYY-MM-DD'), page: 1 }, () => this.load_data(1));

  render_filters = () => {
    const { date_filter_type, start_date, end_date } = this.state;
    return (
      <div className="flex flex-wrap items-end gap-3 justify-center mb-3">
        <CustomSingleSelect
          label="Date range"
          options={date_drop_for_report}
          name="date_filter_type"
          onChange={this.change_handler}
          value={date_filter_type}
          style={{ width: 170 }}
        />
        {date_filter_type === 'CUSTOM' ? (
          <>
            <CustomDatePicker
              label="Start Date"
              value={start_date}
              handleChange={(val) => this.handle_date_change('start_date', val)}
              maxDate={end_date}
            />
            <CustomDatePicker
              label="End Date"
              value={end_date}
              handleChange={(val) => this.handle_date_change('end_date', val)}
              minDate={start_date}
              maxDate={moment().format('YYYY-MM-DD')}
            />
          </>
        ) : null}
      </div>
    );
  };

  render_rows = () =>
    this.state.rows.map((row) => (
      <tr key={row.group_id}>
        <td>
          {moment(row.last_attempt).format(day_format_year)}
          <div className="text-xs">{moment(row.last_attempt).format(time_format)}</div>
        </td>
        <td>
          {row.student_uuid ? (
            <a
              href={`/studentprofile?id=${encodeURIComponent(row.student_uuid)}`}
              target="_blank"
              rel="noopener noreferrer"
              title={row.student_email || 'Open student profile in a new tab'}
            >
              {student_label(row)}
            </a>
          ) : (
            '—'
          )}
        </td>
        <td>{row.event_count}</td>
        <td>{step_label(row.step)}</td>
        <td>{reason_label(row)}</td>
        <td>
          {category_label(row.error_category)}
          <div className="text-xs">
            {is_actionable(row.error_category) ? 'actionable' : 'not ours'}
          </div>
        </td>
        <td>{fmt_amount(row.amount, row.currency)}</td>
        <td>{row.country || '—'}</td>
        <td>{origins_label(row.origins)}</td>
        <td>
          <button
            type="button"
            title="Show every event behind this attempt"
            aria-label="Show events"
            onClick={() => this.setState({ open_group: row })}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 16,
              color: 'var(--text-muted)'
            }}
          >
            &#9432;
          </button>
        </td>
      </tr>
    ));

  render() {
    const { loading, rows, total, page, open_group } = this.state;
    return (
      <div className="common-page-container">
        <CommonHeader title="Payment Failures" />
        <div className="common-page-content">
          {this.render_filters()}
          {loading ? (
            <div className="text-center">Loading…</div>
          ) : (
            <>
              <div className="text-center mb-2">
                {total} payment attempt{total === 1 ? '' : 's'} failed
              </div>
              {rows.length === 0 ? (
                <div className="text-center">No failures in this range.</div>
              ) : (
                <>
                  <table className="table-fixed">
                    <thead>
                      <tr>
                        <th>Last attempt</th>
                        <th>Student</th>
                        <th>Attempts</th>
                        <th>Step</th>
                        <th>Reason</th>
                        <th>Category</th>
                        <th>Amount</th>
                        <th>Country</th>
                        <th>Seen by</th>
                        <th aria-label="Events" />
                      </tr>
                    </thead>
                    <tbody>{this.render_rows()}</tbody>
                  </table>
                  <Paginator
                    curr_page={page}
                    page_limit={Math.ceil(total / PAGE_SIZE)}
                    pageChange={(p) => this.load_data(p)}
                  />
                </>
              )}
            </>
          )}
        </div>
        {open_group ? (
          <EventsModal group={open_group} close={() => this.setState({ open_group: null })} />
        ) : null}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({ iana_timezone: state.home.iana_timezone });

export default connect(mapStateToProps)(withRouter(PaymentFailures));
